import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { UserCircle, Search, Edit2, Eye, EyeOff, MapPin, Phone, ChevronDown, ChevronRight, Plus, Trash2, Building2, Loader2, ExternalLink } from 'lucide-react';
import { API } from '@/App';

const REGIONS = [
  'Ile-de-France', 'Auvergne-Rhone-Alpes', 'Nouvelle-Aquitaine', 'Occitanie',
  'Hauts-de-France', 'Grand Est', "Provence-Alpes-Cote d'Azur", 'Pays de la Loire',
  'Bretagne', 'Normandie', 'Bourgogne-Franche-Comte', 'Centre-Val de Loire',
  'Corse', 'DOM-TOM', 'Autre'
];

const FichesAnnuairePage = () => {
  const [profiles, setProfiles] = useState([]);
  const [allFiches, setAllFiches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRegion, setFilterRegion] = useState('all');
  const [expandedUsers, setExpandedUsers] = useState({});
  const [editDialog, setEditDialog] = useState({ open: false, type: null, data: null, userId: null });
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, fichesRes] = await Promise.all([
        axios.get(`${API}/users`, { headers }),
        axios.get(`${API}/admin/directory-fiches`, { headers }),
      ]);
      const ophtalmos = usersRes.data.filter(u => u.role === 'ophthalmologist');
      setProfiles(ophtalmos);
      setAllFiches(fichesRes.data);
    } catch (err) {
      toast.error('Erreur de chargement');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleExpand = (userId) => {
    setExpandedUsers(prev => ({ ...prev, [userId]: !prev[userId] }));
  };

  const getFichesForUser = (userId) => allFiches.filter(f => f.user_id === userId);

  // Profile visibility toggle
  const handleToggleVisibility = async (profile) => {
    try {
      await axios.put(`${API}/directory/profile/${profile.id}`, {
        visible_in_directory: !profile.visible_in_directory,
      }, { headers });
      toast.success(profile.visible_in_directory ? 'Fiche masquée' : 'Fiche rendue visible');
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  // Fiche visibility toggle
  const handleToggleFicheVisibility = async (fiche) => {
    try {
      await axios.put(`${API}/admin/directory-fiches/${fiche.id}`, {
        visible: !fiche.visible,
      }, { headers });
      toast.success(fiche.visible ? 'Fiche masquée' : 'Fiche rendue visible');
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  // Open edit dialog for profile
  const openEditProfile = (profile) => {
    setEditDialog({
      open: true, type: 'profile', userId: profile.id,
      data: {
        address: profile.address || '', city: profile.city || '',
        postal_code: profile.postal_code || '', region: profile.region || '',
        consultation_phone: profile.consultation_phone || '',
        consultation_hours: profile.consultation_hours || '',
        specialties: Array.isArray(profile.specialties) ? profile.specialties.join(', ') : '',
        bio: profile.bio || '', website: profile.website || '',
        doctolib_url: profile.doctolib_url || '',
        visible_in_directory: profile.visible_in_directory !== false,
        _label: `Dr ${profile.first_name} ${profile.last_name}`,
      },
    });
  };

  // Open edit dialog for fiche
  const openEditFiche = (fiche) => {
    setEditDialog({
      open: true, type: 'fiche', userId: fiche.user_id,
      data: {
        id: fiche.id, title: fiche.title || '', address: fiche.address || '',
        city: fiche.city || '', postal_code: fiche.postal_code || '',
        region: fiche.region || '', consultation_phone: fiche.consultation_phone || '',
        consultation_hours: fiche.consultation_hours || '',
        specialties: Array.isArray(fiche.specialties) ? fiche.specialties.join(', ') : '',
        visible: fiche.visible !== false,
        _label: fiche.title || 'Cabinet',
      },
    });
  };

  // Open new fiche dialog for a user
  const openNewFiche = (userId, userName) => {
    setEditDialog({
      open: true, type: 'new-fiche', userId,
      data: {
        title: '', address: '', city: '', postal_code: '', region: '',
        consultation_phone: '', consultation_hours: '', specialties: '', visible: true,
        _label: `Nouvelle fiche pour ${userName}`,
      },
    });
  };

  // Save profile or fiche
  const handleSave = async () => {
    const { type, data, userId } = editDialog;
    setSaving(true);
    try {
      if (type === 'profile') {
        const specialties = data.specialties ? data.specialties.split(',').map(s => s.trim()).filter(Boolean) : [];
        await axios.put(`${API}/directory/profile/${userId}`, {
          address: data.address, city: data.city, postal_code: data.postal_code,
          region: data.region, consultation_phone: data.consultation_phone,
          consultation_hours: data.consultation_hours, specialties,
          bio: data.bio, website: data.website, doctolib_url: data.doctolib_url,
          visible_in_directory: data.visible_in_directory,
        }, { headers });
        toast.success('Profil mis à jour');
      } else if (type === 'fiche') {
        const specialties = data.specialties ? data.specialties.split(',').map(s => s.trim()).filter(Boolean) : [];
        await axios.put(`${API}/admin/directory-fiches/${data.id}`, {
          title: data.title, address: data.address, city: data.city,
          postal_code: data.postal_code, region: data.region,
          consultation_phone: data.consultation_phone,
          consultation_hours: data.consultation_hours, specialties, visible: data.visible,
        }, { headers });
        toast.success('Fiche mise à jour');
      } else if (type === 'new-fiche') {
        const specialties = data.specialties ? data.specialties.split(',').map(s => s.trim()).filter(Boolean) : [];
        await axios.post(`${API}/admin/directory-fiches`, {
          user_id: userId, title: data.title, address: data.address, city: data.city,
          postal_code: data.postal_code, region: data.region,
          consultation_phone: data.consultation_phone,
          consultation_hours: data.consultation_hours, specialties, visible: data.visible,
        }, { headers });
        toast.success('Fiche créée');
        setExpandedUsers(prev => ({ ...prev, [userId]: true }));
      }
      setEditDialog({ open: false, type: null, data: null, userId: null });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de sauvegarde');
    }
    setSaving(false);
  };

  // Delete a fiche
  const handleDeleteFiche = async (ficheId) => {
    if (!window.confirm('Supprimer cette fiche secondaire ?')) return;
    try {
      await axios.delete(`${API}/admin/directory-fiches/${ficheId}`, { headers });
      toast.success('Fiche supprimée');
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  const updateField = (key, value) => {
    setEditDialog(prev => ({ ...prev, data: { ...prev.data, [key]: value } }));
  };

  // Filter
  const regions = [...new Set(profiles.map(p => p.region).filter(Boolean))].sort();
  const filtered = profiles.filter(p => {
    const matchSearch = `${p.first_name} ${p.last_name} ${p.city || ''} ${p.email}`.toLowerCase().includes(searchTerm.toLowerCase());
    const matchRegion = filterRegion === 'all' || p.region === filterRegion;
    return matchSearch && matchRegion;
  });

  // Stats
  const totalFiches = allFiches.length;
  const usersWithFiches = new Set(allFiches.map(f => f.user_id)).size;

  if (loading) return <AdminLayout><div className="flex justify-center items-center h-full"><Loader2 size={24} className="animate-spin text-[#35AFE4]" /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8 max-w-6xl mx-auto" style={{ fontFamily: "'Nunito', sans-serif" }} data-testid="fiches-annuaire-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center">
              <UserCircle size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: "'Barlow', sans-serif" }}>Fiches Annuaire</h1>
              <p className="text-xs text-gray-500">Gérez les profils et fiches multi-cabinets des praticiens</p>
            </div>
          </div>
          <button
            onClick={async () => {
              if (!confirm("Géocoder toutes les adresses sans coordonnées ? Peut prendre quelques minutes.")) return;
              const t = toast.loading('Géocodage en cours...');
              try {
                const r = await axios.post(`${API}/admin/practitioners/geocode-missing`, {}, { headers });
                toast.dismiss(t);
                const d = r.data;
                toast.success(`${d.geocoded_users} praticiens + ${d.geocoded_fiches} fiches géocodés (${d.failed_count} échecs)`);
              } catch {
                toast.dismiss(t);
                toast.error('Erreur lors du géocodage');
              }
            }}
            className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-xl text-xs font-semibold transition-colors"
            data-testid="btn-geocode-missing"
            title="Calcule les coordonnées GPS (lat/lng) pour toutes les adresses sans géolocalisation afin d'afficher tous les praticiens sur la carte">
            <MapPin size={14} /> Géocoder les adresses manquantes
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <p className="text-[10px] text-gray-400 uppercase tracking-wider font-bold mb-1">Praticiens</p>
            <p className="text-2xl font-bold text-gray-900">{profiles.length}</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <p className="text-[10px] text-gray-400 uppercase tracking-wider font-bold mb-1">Visibles</p>
            <p className="text-2xl font-bold text-emerald-600">{profiles.filter(p => p.visible_in_directory).length}</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <p className="text-[10px] text-gray-400 uppercase tracking-wider font-bold mb-1">Fiches secondaires</p>
            <p className="text-2xl font-bold text-[#35AFE4]">{totalFiches}</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <p className="text-[10px] text-gray-400 uppercase tracking-wider font-bold mb-1">Multi-cabinets</p>
            <p className="text-2xl font-bold text-violet-600">{usersWithFiches}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-3 mb-6">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-[#35AFE4] outline-none bg-white"
              placeholder="Rechercher un praticien..." data-testid="search-fiches" />
          </div>
          <div className="relative">
            <select value={filterRegion} onChange={e => setFilterRegion(e.target.value)}
              className="appearance-none pl-3 pr-8 py-2.5 rounded-xl border border-gray-200 text-sm bg-white" data-testid="filter-region">
              <option value="all">Toutes les régions</option>
              {regions.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Practitioners list with expandable fiches */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" data-testid="practitioners-table">
          {filtered.length === 0 ? (
            <div className="text-center py-16">
              <UserCircle size={40} className="mx-auto text-gray-300 mb-3" />
              <p className="text-sm text-gray-400">Aucun praticien trouvé</p>
            </div>
          ) : (
            <div>
              {/* Table header */}
              <div className="grid grid-cols-12 gap-2 px-5 py-3 border-b border-gray-100 bg-gray-50/50">
                <div className="col-span-4 text-xs font-bold text-gray-500 uppercase">Praticien</div>
                <div className="col-span-2 text-xs font-bold text-gray-500 uppercase">Ville</div>
                <div className="col-span-2 text-xs font-bold text-gray-500 uppercase">Région</div>
                <div className="col-span-1 text-xs font-bold text-gray-500 uppercase text-center">Fiches</div>
                <div className="col-span-1 text-xs font-bold text-gray-500 uppercase text-center">Visible</div>
                <div className="col-span-2 text-xs font-bold text-gray-500 uppercase text-right">Actions</div>
              </div>

              {/* Rows */}
              {filtered.map((p) => {
                const userFiches = getFichesForUser(p.id);
                const isExpanded = expandedUsers[p.id];
                const hasMultiple = userFiches.length > 0;

                return (
                  <div key={p.id} data-testid={`practitioner-row-${p.id}`}>
                    {/* Main row */}
                    <div className={`grid grid-cols-12 gap-2 px-5 py-3.5 items-center border-b border-gray-50 hover:bg-gray-50/50 transition-colors ${isExpanded ? 'bg-[#fafbfe]' : ''}`}>
                      {/* Name */}
                      <div className="col-span-4 flex items-center gap-3 min-w-0">
                        <button
                          onClick={() => hasMultiple && toggleExpand(p.id)}
                          className={`flex-shrink-0 w-5 h-5 flex items-center justify-center rounded ${hasMultiple ? 'hover:bg-gray-200 cursor-pointer text-gray-500' : 'text-gray-200 cursor-default'}`}
                          data-testid={`expand-btn-${p.id}`}
                          disabled={!hasMultiple}
                        >
                          {hasMultiple ? (isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />) : <span className="w-2 h-2 rounded-full bg-gray-200" />}
                        </button>
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#35AFE4] to-[#1e3a5f] flex items-center justify-center text-white text-xs font-bold flex-shrink-0 overflow-hidden">
                          {p.profile_image ? <img src={`${process.env.REACT_APP_BACKEND_URL}${p.profile_image}`} alt="" className="w-full h-full object-cover" /> : `${p.first_name?.[0] || ''}${p.last_name?.[0] || ''}`}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-sm text-gray-900 truncate">Dr {p.first_name} {p.last_name}</p>
                          <p className="text-[11px] text-gray-400 truncate">{p.email}</p>
                        </div>
                      </div>

                      {/* City */}
                      <div className="col-span-2 text-xs text-gray-600">
                        {p.city && <span className="flex items-center gap-1"><MapPin size={11} className="text-gray-400" /> {p.city}</span>}
                      </div>

                      {/* Region */}
                      <div className="col-span-2">
                        {p.region && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-700">{p.region}</span>}
                      </div>

                      {/* Fiches count */}
                      <div className="col-span-1 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          hasMultiple ? 'bg-[#eaf6fc] text-[#35AFE4]' : 'bg-gray-100 text-gray-400'
                        }`}>
                          <Building2 size={10} />
                          {1 + userFiches.length}
                        </span>
                      </div>

                      {/* Visibility */}
                      <div className="col-span-1 text-center">
                        <button onClick={() => handleToggleVisibility(p)}
                          className={`p-1.5 rounded-lg transition-colors ${p.visible_in_directory ? 'text-emerald-600 hover:bg-emerald-50' : 'text-gray-400 hover:bg-gray-100'}`}
                          data-testid={`toggle-vis-${p.id}`}>
                          {p.visible_in_directory ? <Eye size={16} /> : <EyeOff size={16} />}
                        </button>
                      </div>

                      {/* Actions */}
                      <div className="col-span-2 flex items-center justify-end gap-1">
                        <button onClick={() => openNewFiche(p.id, `Dr ${p.first_name} ${p.last_name}`)}
                          className="p-1.5 rounded-lg hover:bg-[#eaf6fc] text-gray-400 hover:text-[#35AFE4] transition-colors" title="Ajouter une fiche"
                          data-testid={`add-fiche-${p.id}`}>
                          <Plus size={14} />
                        </button>
                        <a href={`/annuaire/${p.id}`} target="_blank" rel="noreferrer"
                          className="p-1.5 rounded-lg hover:bg-blue-50 text-gray-400 hover:text-blue-600 transition-colors" title="Voir la fiche publique">
                          <ExternalLink size={14} />
                        </a>
                        <button onClick={() => openEditProfile(p)}
                          className="p-1.5 rounded-lg hover:bg-blue-50 text-gray-400 hover:text-blue-600 transition-colors" title="Modifier le profil"
                          data-testid={`edit-profile-${p.id}`}>
                          <Edit2 size={14} />
                        </button>
                      </div>
                    </div>

                    {/* Expanded fiches */}
                    {isExpanded && userFiches.length > 0 && (
                      <div className="bg-[#f8f9fb] border-b border-gray-100" data-testid={`fiches-list-${p.id}`}>
                        {userFiches.map((fiche) => (
                          <div key={fiche.id} className="grid grid-cols-12 gap-2 px-5 py-2.5 items-center border-b border-gray-100/50 last:border-b-0 ml-8">
                            {/* Fiche title */}
                            <div className="col-span-4 flex items-center gap-2 min-w-0">
                              <div className="w-7 h-7 rounded-lg bg-[#eaf6fc] flex items-center justify-center flex-shrink-0">
                                <Building2 size={12} className="text-[#35AFE4]" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-700 truncate">{fiche.title || 'Sans titre'}</p>
                                <p className="text-[10px] text-gray-400">Fiche secondaire</p>
                              </div>
                            </div>

                            {/* City */}
                            <div className="col-span-2 text-xs text-gray-500">
                              {fiche.city && <span className="flex items-center gap-1"><MapPin size={10} className="text-gray-300" /> {fiche.city}</span>}
                            </div>

                            {/* Region */}
                            <div className="col-span-2">
                              {fiche.region && <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-500">{fiche.region}</span>}
                            </div>

                            {/* Phone */}
                            <div className="col-span-1 text-xs text-gray-400">
                              {fiche.consultation_phone && <Phone size={10} className="inline mr-1" />}
                            </div>

                            {/* Visibility */}
                            <div className="col-span-1 text-center">
                              <button onClick={() => handleToggleFicheVisibility(fiche)}
                                className={`p-1 rounded-lg transition-colors ${fiche.visible ? 'text-emerald-500 hover:bg-emerald-50' : 'text-gray-300 hover:bg-gray-100'}`}
                                data-testid={`toggle-fiche-vis-${fiche.id}`}>
                                {fiche.visible ? <Eye size={14} /> : <EyeOff size={14} />}
                              </button>
                            </div>

                            {/* Actions */}
                            <div className="col-span-2 flex items-center justify-end gap-1">
                              <button onClick={() => openEditFiche(fiche)}
                                className="p-1.5 rounded-lg hover:bg-blue-50 text-gray-400 hover:text-blue-600 transition-colors"
                                data-testid={`edit-fiche-${fiche.id}`}>
                                <Edit2 size={12} />
                              </button>
                              <button onClick={() => handleDeleteFiche(fiche.id)}
                                className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                                data-testid={`delete-fiche-${fiche.id}`}>
                                <Trash2 size={12} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Edit/Create Dialog */}
        <Dialog open={editDialog.open} onOpenChange={(v) => !v && setEditDialog({ open: false, type: null, data: null, userId: null })}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-[#1e3a5f]" style={{ fontFamily: "'Barlow', sans-serif" }}>
                {editDialog.type === 'profile' ? 'Modifier le profil principal' :
                 editDialog.type === 'fiche' ? 'Modifier la fiche' :
                 'Ajouter une fiche secondaire'}
              </DialogTitle>
              {editDialog.data?._label && (
                <p className="text-xs text-gray-400 mt-1">{editDialog.data._label}</p>
              )}
            </DialogHeader>
            {editDialog.data && (
              <div className="space-y-3 mt-2">
                {/* Title (only for fiches) */}
                {editDialog.type !== 'profile' && (
                  <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase block mb-1">Nom du cabinet</label>
                    <input value={editDialog.data.title || ''} onChange={e => updateField('title', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#35AFE4] outline-none"
                      placeholder="Ex: Cabinet Optic Paris 15" data-testid="edit-title" />
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase block mb-1">Adresse</label>
                    <input value={editDialog.data.address || ''} onChange={e => updateField('address', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" data-testid="edit-address" />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase block mb-1">Ville</label>
                    <input value={editDialog.data.city || ''} onChange={e => updateField('city', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" data-testid="edit-city" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase block mb-1">Code postal</label>
                    <input value={editDialog.data.postal_code || ''} onChange={e => updateField('postal_code', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" data-testid="edit-postal" />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase block mb-1">Région</label>
                    <select value={editDialog.data.region || ''} onChange={e => updateField('region', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" data-testid="edit-region">
                      <option value="">-- Sélectionnez --</option>
                      {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase block mb-1">Téléphone</label>
                    <input value={editDialog.data.consultation_phone || ''} onChange={e => updateField('consultation_phone', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" data-testid="edit-phone" />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase block mb-1">Horaires</label>
                    <input value={editDialog.data.consultation_hours || ''} onChange={e => updateField('consultation_hours', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" placeholder="Lun-Ven 9h-18h" />
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase block mb-1">Spécialités (séparées par des virgules)</label>
                  <input value={editDialog.data.specialties || ''} onChange={e => updateField('specialties', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" placeholder="Contactologie, Kératocône..."
                    data-testid="edit-specialties" />
                </div>

                {/* Profile-only fields */}
                {editDialog.type === 'profile' && (
                  <>
                    <div>
                      <label className="text-[10px] font-bold text-gray-400 uppercase block mb-1">Biographie</label>
                      <textarea value={editDialog.data.bio || ''} onChange={e => updateField('bio', e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm h-20 resize-none" data-testid="edit-bio" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-[10px] font-bold text-gray-400 uppercase block mb-1">Site web</label>
                        <input value={editDialog.data.website || ''} onChange={e => updateField('website', e.target.value)}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" placeholder="https://..." />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-gray-400 uppercase block mb-1">Doctolib</label>
                        <input value={editDialog.data.doctolib_url || ''} onChange={e => updateField('doctolib_url', e.target.value)}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" placeholder="https://doctolib.fr/..." />
                      </div>
                    </div>
                  </>
                )}

                {/* Visibility */}
                <div className="flex items-center gap-2 pt-1">
                  <input type="checkbox" id="edit-vis"
                    checked={editDialog.type === 'profile' ? editDialog.data.visible_in_directory !== false : editDialog.data.visible !== false}
                    onChange={e => updateField(editDialog.type === 'profile' ? 'visible_in_directory' : 'visible', e.target.checked)}
                    className="rounded" />
                  <label htmlFor="edit-vis" className="text-sm text-gray-700">Visible dans l'annuaire public</label>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-3">
                  <button onClick={() => setEditDialog({ open: false, type: null, data: null, userId: null })}
                    className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50">
                    Annuler
                  </button>
                  <button onClick={handleSave} disabled={saving}
                    className="flex-1 px-4 py-2.5 rounded-xl bg-[#1e3a5f] text-white text-sm font-bold hover:bg-[#162d4a] disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="save-edit-btn">
                    {saving && <Loader2 size={14} className="animate-spin" />}
                    {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                  </button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default FichesAnnuairePage;
